import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {switchMap, take, tap} from 'rxjs/operators';
import {AuthService} from './auth.service';

/**
 * Strażnik routingu (Route Guard) chroniący dostęp do widoku szczegółów zamówienia.
 * Implementuje zaawansowany wzorzec "Deep Linking / Return URL", który w przypadku
 * braku aktywnej sesji próbuje automatycznie odtworzyć token, a w ostateczności
 * przekierowuje na ekran logowania, zapamiętując docelowy adres URL, aby
 * wrócić do niego po pomyślnym zalogowaniu.
 */
@Injectable({
  providedIn: 'root'
})
export class OrderDetailsAuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {
  }

  /**
   * Determinuje, czy użytkownik ma prawo aktywować docelową ścieżkę.
   * Wykorzystuje reaktywne strumienie (RxJS) do asynchronicznej weryfikacji tożsamości.
   *
   * @param route Zrzut informacji o docelowej ścieżce (Route).
   * @param state Zrzut pełnego stanu routera (zawiera m.in. pełny adres URL).
   * @returns Observable emitujący wartość logiczną, określającą prawo dostępu.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.authService.userIsAuthenticated.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return this.authService.autoLoginWithReturnUrl({queryParams: {returnUrl: state.url},
          });
        } else {
          // authenticated check token
          return of(isAuthenticated);
        }
      }),
      tap(isAuthenticated => {
        if (!isAuthenticated) {
          this.router.navigate(['signin'], {queryParams: {returnUrl: state.url}});
        }
      })
    );
  }
}
